import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { T } from "./adminTheme";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Local helpers — theme-aware
// ─────────────────────────────────────────────────────────────────────────────

function FieldLabel({ children }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  return (
    <Typography sx={{
      fontSize: 10,
      fontWeight: 700,
      color: t.muted,
      mb: 0.5,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}>
      {children}
    </Typography>
  );
}

function FilterField({ label, value, onChange, type = "text" }) {
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={type}
      value={value}
      onChange={onChange}
      InputLabelProps={
        type === "date" || type === "datetime-local" ? { shrink: true } : undefined
      }
    />
  );
}

function StatusChip({ label, color = "default" }) {
  return <Chip size="small" label={label} color={color} variant="filled" />;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch {
    return String(value);
  }
}

function getCaseExplanation(caseCode) {
  const cases = {
    CASE_01: "Open shift + open job + job operations still open. Cleanup closes job operations, job summary, shift, then releases resources.",
    CASE_02: "Open shift + open job + both job and non-job operations still open. Cleanup closes all open operations first, then job, shift, and resources.",
    CASE_03: "Open shift + open job, but no open operations. Cleanup closes job summary, shift, and resources.",
    CASE_04: "Open shift + open job + non-job operations open. Cleanup closes non-job operations, then job summary, shift, and resources.",
    CASE_05: "Open shift + non-job operations only. Cleanup closes non-job operations, shift, and resources.",
    CASE_06: "Only shift is open. Cleanup closes shift and releases resources.",
    CASE_07: "No open shift, but job summary and job operations are open. Cleanup closes job operations, job summary, and resources.",
    CASE_08: "No open shift, but job summary is open. Cleanup closes job summary and releases resources.",
    CASE_09: "No open shift/job summary, but job operations are open. Cleanup closes job operations and releases resources.",
    CASE_10: "No open shift/job summary, but non-job operations are open. Cleanup closes non-job operations and releases resources.",
    CASE_11: "Open shift + job operations open, but no open job summary. Cleanup closes job operations, shift, and resources.",
    CASE_12: "Open shift + job and non-job operations open, but no open job summary. Cleanup closes all operations, shift, and resources.",
    CASE_13: "Shift is already logged out, but job summary and operations are still open. Cleanup closes dirty operations/job records and keeps/corrects shift logout time only if needed.",
    CASE_14: "Shift is already logged out, but job summary is still open. Cleanup inserts missing JOB_END if needed, closes job summary, and releases resources.",
    CASE_15: "Shift is already logged out, but operation records are still open. Cleanup closes dirty operation records and releases resources.",
    CASE_00: "No clear stale session was detected.",
  };
  return cases[caseCode] || "Unknown recovery case.";
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function SessionRecoveryTab({
  onSnack,
  adminUsername,
  initialFilter,
  onInitialFilterApplied,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [scope, setScope] = useState("stale_before_date");
  const [filters, setFilters] = useState({
    employeeId: "",
    machineCode: "",
    shiftDate: "",
    beforeDate: new Date().toISOString().slice(0, 10),
  });

  const [machineOptions, setMachineOptions] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [reason, setReason] = useState("");
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeData, setAnalyzeData] = useState(null);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Apply initial filter when redirected from another tab
  useEffect(() => {
    if (!initialFilter) return;
    if (initialFilter.employeeId) {
      setScope("employee");
      setFilters((prev) => ({ ...prev, employeeId: initialFilter.employeeId }));
    } else if (initialFilter.machineCode) {
      setScope("machine");
      setFilters((prev) => ({ ...prev, machineCode: initialFilter.machineCode }));
    }
    if (typeof onInitialFilterApplied === "function") onInitialFilterApplied();
  }, [initialFilter, onInitialFilterApplied]);

  const buildParams = useCallback(() => {
    const params = { scope };
    if (scope === "employee" && filters.employeeId)
      params.employeeId = filters.employeeId;
    if ((scope === "machine" || scope === "machine_today") && filters.machineCode)
      params.machineCode = filters.machineCode;
    if ((scope === "shift_date" || scope === "machine_today") && filters.shiftDate)
      params.shiftDate = filters.shiftDate;
    if (scope === "stale_before_date" && filters.beforeDate)
      params.beforeDate = filters.beforeDate;
    return params;
  }, [scope, filters]);

  const loadMachines = useCallback(async () => {
    setMachinesLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/machines`);
      setMachineOptions(res.data || []);
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to load machine suggestions.", "error");
    } finally {
      setMachinesLoading(false);
    }
  }, [onSnack]);

  useEffect(() => { loadMachines(); }, [loadMachines]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/session-recovery/list`, {
        params: buildParams(),
      });
      setRows(res.data.rows || []);
      setSelectedKeys({});
    } catch (err) {
      onSnack(err?.response?.data?.error || "Failed to load recovery list.", "error");
    } finally {
      setLoading(false);
    }
  }, [buildParams, onSnack]);

  const selectedItems = useMemo(
    () => rows.filter((r) => selectedKeys[`${r.employeeId}-${r.machineCode}`]),
    [rows, selectedKeys]
  );

  const toggleSelect = (row) => {
    const key = `${row.employeeId}-${row.machineCode}`;
    setSelectedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === rows.length) { setSelectedKeys({}); return; }
    const next = {};
    rows.forEach((r) => { next[`${r.employeeId}-${r.machineCode}`] = true; });
    setSelectedKeys(next);
  };

  const analyzeOne = async (row) => {
    setAnalyzeLoading(true);
    setAnalyzeOpen(true);
    setAnalyzeData(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/session-recovery/analyze`, {
        params: { employeeId: row.employeeId, machineCode: row.machineCode },
      });
      setAnalyzeData(res.data);
    } catch (err) {
      onSnack(err?.response?.data?.error || "Failed to analyze session.", "error");
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const cleanupOne = async (row) => {
    if (!reason.trim()) { onSnack("Cleanup reason is required.", "warning"); return; }
    setCleanupLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/admin/session-recovery/cleanup`, {
        employeeId: row.employeeId,
        machineCode: row.machineCode,
        adminUser: adminUsername,
        reason,
        cleanupScope: scope,
      });
      onSnack(res.data.message || "Cleanup completed successfully.");
      fetchList();
    } catch (err) {
      onSnack(err?.response?.data?.error || "Cleanup failed.", "error");
    } finally {
      setCleanupLoading(false);
    }
  };

  const cleanupSelected = async () => {
    if (!selectedItems.length) { onSnack("Please select at least one session.", "warning"); return; }
    if (!reason.trim()) { onSnack("Cleanup reason is required.", "warning"); return; }
    setCleanupLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/admin/session-recovery/cleanup-bulk`, {
        items: selectedItems.map((r) => ({ employeeId: r.employeeId, machineCode: r.machineCode })),
        adminUser: adminUsername,
        reason,
        cleanupScope: scope,
      });
      onSnack(res.data.message || "Bulk cleanup completed successfully.");
      fetchList();
    } catch (err) {
      onSnack(err?.response?.data?.error || "Bulk cleanup failed.", "error");
    } finally {
      setCleanupLoading(false);
    }
  };

  // ── Shared card sx ─────────────────────────────────────────────────────────
  const cardSx = {
    borderRadius: "14px",
    border: `0.5px solid ${t.border}`,
    background: t.s0,
    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)" : "none",
    transition: "background 0.25s ease",
  };

  // ── Table head sx ──────────────────────────────────────────────────────────
  const thSx = {
    background: t.s1,
    color: t.muted,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderBottom: `0.5px solid ${t.border}`,
    padding: "10px 12px",
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  const tdSx = {
    color: t.primary,
    fontSize: 12,
    borderBottom: `0.5px solid ${t.border}`,
    padding: "10px 12px",
    verticalAlign: "middle",
  };

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Box>

      {/* ══ Filter card ═══════════════════════════════════════════════════════ */}
      <Paper sx={{ ...cardSx, p: 0, overflow: "hidden", mb: 2 }}>

        {/* Card header */}
        <Box sx={{
          px: 3,
          py: 2,
          borderBottom: `0.5px solid ${t.border}`,
          background: isDark
            ? "linear-gradient(135deg, rgba(139,92,246,0.06) 0%, transparent 60%)"
            : "transparent",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}>
          {/* Icon badge — violet theme for maintenance */}
          <Box sx={{
            width: 34,
            height: 34,
            borderRadius: "9px",
            background: isDark
              ? "rgba(139,92,246,0.15)"
              : "rgba(124,58,237,0.08)",
            border: `0.5px solid ${isDark ? "rgba(139,92,246,0.30)" : "rgba(124,58,237,0.20)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: t.violet,
            flexShrink: 0,
            boxShadow: isDark ? "0 0 10px rgba(139,92,246,0.15)" : "none",
          }}>
            <CleaningServicesIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontSize: 15,
              fontWeight: 400,
              color: t.heading,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}>
              Analyze and clean stale sessions, including already closed shifts with
              unfinished operation or job records.
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FieldLabel>Cleanup Scope</FieldLabel>
              <FormControl fullWidth size="small">
                <Select value={scope} onChange={(e) => setScope(e.target.value)}>
                  <MenuItem value="employee">Single Employee</MenuItem>
                  <MenuItem value="machine">Single Workstation</MenuItem>
                  <MenuItem value="machine_today">Today — Specific Workstation</MenuItem>
                  <MenuItem value="shift_date">Specific Shift Date</MenuItem>
                  <MenuItem value="stale_before_date">All Past-Day Stale Sessions</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {scope === "employee" && (
              <Grid item xs={12} md={3}>
                <FieldLabel>Employee ID</FieldLabel>
                <FilterField
                  label="Employee ID"
                  value={filters.employeeId}
                  onChange={(e) => setFilters((f) => ({ ...f, employeeId: e.target.value }))}
                />
              </Grid>
            )}

            {(scope === "machine" || scope === "machine_today") && (
              <Grid item xs={12} md={3}>
                <FieldLabel>Machine Code</FieldLabel>
                <Autocomplete
                  size="small"
                  loading={machinesLoading}
                  options={machineOptions}
                  value={machineOptions.find((m) => m.MachineCode === filters.machineCode) || null}
                  onChange={(_, newValue) =>
                    setFilters((f) => ({ ...f, machineCode: newValue?.MachineCode || "" }))
                  }
                  getOptionLabel={(option) =>
                    option ? `${option.MachineCode} - ${option.MachineDescription || ""}` : ""
                  }
                  isOptionEqualToValue={(option, value) => option.MachineCode === value.MachineCode}
                  renderInput={(params) => (
                    <TextField {...params} label="Machine Code" placeholder="Select machine" />
                  )}
                />
              </Grid>
            )}

            {(scope === "shift_date" || scope === "machine_today") && (
              <Grid item xs={12} md={3}>
                <FieldLabel>Shift Date</FieldLabel>
                <FilterField
                  label="Shift Date"
                  type="date"
                  value={filters.shiftDate}
                  onChange={(e) => setFilters((f) => ({ ...f, shiftDate: e.target.value }))}
                />
              </Grid>
            )}

            {scope === "stale_before_date" && (
              <Grid item xs={12} md={3}>
                <FieldLabel>Before Date</FieldLabel>
                <FilterField
                  label="Before Date"
                  type="date"
                  value={filters.beforeDate}
                  onChange={(e) => setFilters((f) => ({ ...f, beforeDate: e.target.value }))}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FieldLabel>Cleanup Reason</FieldLabel>
              <TextField
                fullWidth
                size="small"
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Browser closed — stale session cleanup"
              />
            </Grid>
          </Grid>

          {/* Action buttons */}
          <Box sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2.5,
            pt: 2,
            borderTop: `0.5px solid ${t.border}`,
          }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchList}
              disabled={loading}
              sx={{ fontWeight: 600, fontSize: 13 }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={loading
                ? <CircularProgress size={15} color="inherit" />
                : <SearchIcon />
              }
              onClick={fetchList}
              disabled={loading}
              sx={{ fontWeight: 600, fontSize: 13 }}
            >
              {loading ? "Loading…" : "Analyze Scope"}
            </Button>

            <Button
              variant="contained"
              startIcon={cleanupLoading
                ? <CircularProgress size={15} color="inherit" />
                : <CleaningServicesIcon />
              }
              onClick={cleanupSelected}
              disabled={cleanupLoading || !rows.length}
              sx={{
                fontWeight: 600,
                fontSize: 13,
                background: isDark
                  ? "rgba(245,158,11,0.15)"
                  : "rgba(245,158,11,0.10)",
                color: isDark ? "#fbbf24" : "#d97706",
                border: `0.5px solid ${t.amber}40`,
                boxShadow: "none",
                "&:hover": {
                  background: isDark ? "rgba(245,158,11,0.22)" : "rgba(245,158,11,0.16)",
                  boxShadow: "none",
                },
                "&:disabled": { opacity: 0.4 },
              }}
            >
              {cleanupLoading ? "Cleaning…" : "Cleanup Selected"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ══ Results card ══════════════════════════════════════════════════════ */}
      <Paper sx={{ ...cardSx, overflow: "hidden" }}>

        {/* Card header */}
        <Box sx={{
          px: 3,
          py: 1.75,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `0.5px solid ${t.border}`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: 14,
              color: t.heading,
              letterSpacing: "-0.01em",
            }}>
              Recovery Candidates
            </Typography>
            {/* Count badge */}
            <Box sx={{
              px: 1.25,
              py: 0.2,
              borderRadius: "20px",
              background: isDark ? t.s3 : t.s2,
              border: `0.5px solid ${t.border}`,
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.secondary }}>
                {rows.length}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {selectedItems.length > 0 && (
              <Box sx={{
                px: 1.25,
                py: 0.3,
                borderRadius: "6px",
                background: t.blueAlpha,
                border: `0.5px solid ${t.blue}35`,
              }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDark ? "#93c5fd" : t.blue }}>
                  {selectedItems.length} selected
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <CircularProgress size={26} sx={{ color: t.blue }} />
            <Typography sx={{ fontSize: 12, color: t.muted, mt: 1.5, fontWeight: 500 }}>
              Loading candidates…
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ ...thSx, pl: 2 }}>
                    <Checkbox
                      checked={rows.length > 0 && selectedItems.length === rows.length}
                      indeterminate={selectedItems.length > 0 && selectedItems.length < rows.length}
                      onChange={toggleSelectAll}
                      size="small"
                      sx={{ color: t.muted, "&.Mui-checked": { color: t.blue } }}
                    />
                  </TableCell>
                  {["Employee ID", "Machine", "Shift Login", "Shift Status", "Job No", "Open Shift", "Open Job", "Job Ops", "Non-Job Ops", "Case", "Job Markers", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ ...thSx, ...(h === "Actions" ? { textAlign: "right" } : {}) }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={13}
                      sx={{
                        textAlign: "center",
                        py: 6,
                        color: t.muted,
                        fontSize: 13,
                        fontWeight: 500,
                        borderBottom: "none",
                      }}
                    >
                      No candidate sessions found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const key = `${row.employeeId}-${row.machineCode}`;
                    const isSelected = !!selectedKeys[key];
                    return (
                      <TableRow
                        key={key}
                        sx={{
                          transition: "background 0.12s ease",
                          "&:hover": { background: t.s2 },
                          background: isSelected
                            ? (isDark ? "rgba(59,130,246,0.06)" : "rgba(37,99,235,0.04)")
                            : "transparent",
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ ...tdSx, pl: 2 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelect(row)}
                            size="small"
                            sx={{ color: t.muted, "&.Mui-checked": { color: t.blue } }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...tdSx, fontFamily: "monospace", fontWeight: 700, color: t.heading }}>
                          {row.employeeId}
                        </TableCell>
                        <TableCell sx={tdSx}>{row.machineCode}</TableCell>
                        <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                          {formatDateTime(row.shiftLoggedIn)}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {row.hasOpenShift ? (
                            <StatusChip label="Open" color="warning" />
                          ) : row.hasAnyShift ? (
                            <StatusChip label={row.latestShiftStatus || "Closed"} color="default" />
                          ) : (
                            <StatusChip label="No Shift" color="error" />
                          )}
                        </TableCell>
                        <TableCell sx={{ ...tdSx, fontFamily: "monospace" }}>
                          {row.jobNo || "—"}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {row.hasOpenShift
                            ? <StatusChip label="Yes" color="warning" />
                            : <StatusChip label="No" />}
                        </TableCell>
                        <TableCell sx={tdSx}>
                          {row.hasOpenJobsummary
                            ? <StatusChip label="Yes" color="error" />
                            : <StatusChip label="No" />}
                        </TableCell>
                        <TableCell sx={tdSx}>{row.ongoingJobOpsCount}</TableCell>
                        <TableCell sx={tdSx}>{row.ongoingNonJobOpsCount}</TableCell>
                        <TableCell sx={{ ...tdSx, maxWidth: 260 }}>
                          <StatusChip label={row.caseCode} color="primary" />
                          <Typography sx={{
                            fontSize: 10,
                            color: t.muted,
                            mt: 0.5,
                            lineHeight: 1.4,
                            fontWeight: 500,
                          }}>
                            {getCaseExplanation(row.caseCode)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              label={row.jobMarkers?.hasJobStartedMarker ? "JOB_STARTED" : "No Start"}
                              color={row.jobMarkers?.hasJobStartedMarker ? "success" : "default"}
                            />
                            <Chip
                              size="small"
                              label={row.jobMarkers?.hasJobEndedMarker ? "JOB_END" : "No End"}
                              color={row.jobMarkers?.hasJobEndedMarker ? "success" : "warning"}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...tdSx, textAlign: "right", whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => analyzeOne(row)}
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "7px",
                                color: isDark ? "#93c5fd" : t.blue,
                                background: t.blueAlpha,
                                border: `0.5px solid ${t.blue}30`,
                                "&:hover": { background: isDark ? "rgba(59,130,246,0.18)" : "rgba(37,99,235,0.12)" },
                              }}
                            >
                              Analyze
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => cleanupOne(row)}
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "7px",
                                color: isDark ? "#fbbf24" : "#d97706",
                                background: t.amberAlpha,
                                border: `0.5px solid ${t.amber}30`,
                                "&:hover": { background: isDark ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.12)" },
                              }}
                            >
                              Cleanup
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ══ Analyze dialog ════════════════════════════════════════════════════ */}
      <Dialog
        open={analyzeOpen}
        onClose={() => setAnalyzeOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 15,
          fontWeight: 700,
          color: t.heading,
          borderBottom: `0.5px solid ${t.border}`,
          px: 2.5,
          py: 2,
        }}>
          Session Analysis
          <IconButton
            size="small"
            onClick={() => setAnalyzeOpen(false)}
            sx={{
              color: t.muted,
              width: 30,
              height: 30,
              borderRadius: "7px",
              "&:hover": { background: t.s2 },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          {analyzeLoading ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <CircularProgress size={26} sx={{ color: t.blue }} />
              <Typography sx={{ fontSize: 12, color: t.muted, mt: 1.5 }}>
                Analyzing session…
              </Typography>
            </Box>
          ) : analyzeData ? (
            <Box sx={{ display: "grid", gap: 1.5, py: 1 }}>
              <Alert severity="info">
                Case: <strong>{analyzeData.caseCode}</strong>
              </Alert>
              <Alert severity="warning">
                {getCaseExplanation(analyzeData.caseCode)}
              </Alert>

              {/* Detail grid */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                p: 2,
                borderRadius: "10px",
                background: t.s2,
                border: `0.5px solid ${t.border}`,
              }}>
                {[
                  ["Employee", analyzeData.employeeId],
                  ["Machine", analyzeData.machineCode || "—"],
                  ["Open Shift", analyzeData.hasOpenShift ? "Yes" : "No"],
                  ["Any Shift Found", analyzeData.hasAnyShift ? "Yes" : "No"],
                  ["Latest Shift Status", analyzeData.latestShiftStatus || "—"],
                  ["Open Job Summary", analyzeData.hasOpenJobsummary ? "Yes" : "No"],
                  ["Ongoing Job Ops", analyzeData.ongoingJobOpsCount],
                  ["Ongoing Non-Job Ops", analyzeData.ongoingNonJobOpsCount],
                  ["Shift In", formatDateTime(analyzeData.latestShift?.shift_loggedIn_DateTime)],
                  ["Shift Out", formatDateTime(analyzeData.latestShift?.shift_loggedOut_DateTime)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: t.muted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.3 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: t.primary, fontWeight: 500 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 0.5 }} />

              {/* Job markers */}
              <Box sx={{
                p: 1.75,
                borderRadius: "10px",
                background: t.s2,
                border: `0.5px solid ${t.border}`,
              }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: t.muted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 1 }}>
                  Job Markers
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    size="small"
                    label={`JOB_STARTED: ${analyzeData.jobMarkers?.hasJobStartedMarker ? "Yes" : "No"}`}
                    color={analyzeData.jobMarkers?.hasJobStartedMarker ? "success" : "default"}
                  />
                  <Chip
                    size="small"
                    label={`JOB_END: ${analyzeData.jobMarkers?.hasJobEndedMarker ? "Yes" : "No"}`}
                    color={analyzeData.jobMarkers?.hasJobEndedMarker ? "success" : "warning"}
                  />
                </Box>
              </Box>

              {/* Open job summary */}
              {analyzeData.openJobsummary && (
                <Box sx={{
                  p: 1.75,
                  borderRadius: "10px",
                  background: t.s2,
                  border: `0.5px solid ${t.border}`,
                  borderLeft: `2.5px solid ${t.amber}`,
                }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: t.amber, letterSpacing: "0.08em", textTransform: "uppercase", mb: 1 }}>
                    Open Job Summary
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                    {[
                      ["Job No", analyzeData.openJobsummary?.jobNo || "—"],
                      ["Customer", analyzeData.openJobsummary?.customer || "—"],
                      ["Product", analyzeData.openJobsummary?.product || "—"],
                      ["Start", formatDateTime(analyzeData.openJobsummary?.startDatetime)],
                    ].map(([label, value]) => (
                      <Box key={label}>
                        <Typography sx={{ fontSize: 9, fontWeight: 700, color: t.muted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.3 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: t.primary, fontWeight: 500 }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}